import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, Badge, ProgressBar } from 'react-bootstrap';
import axios from 'axios';

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'short', year: '2-digit' });
};

function Stage({ label, value, of, hint }) {
  const share = of > 0 ? Math.round((value * 100) / of) : 0;
  return (
    <div className="mb-2" data-testid={`funnel-stage-${label}`}>
      <div className="d-flex justify-content-between">
        <span>{label}</span>
        <span><strong>{value}</strong>{of > 0 && <span className="text-muted"> ({share}%)</span>}</span>
      </div>
      <ProgressBar now={share} style={{ height: '0.5rem' }} />
      {hint && <div className="small text-muted">{hint}</div>}
    </div>
  );
}

/** The customer funnel on the System Admin and Tech Support dashboard: sign-ups to paying, who brought them, and how long to the target. */
export default function SalesFunnel() {
  const [f, setF] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get('/api/system-admin/funnel').then((r) => setF(r.data)).catch(() => setFailed(true));
  }, []);

  if (failed) return null;
  if (!f) return null;

  const maxSignups = Math.max(1, ...f.months.map((m) => m.signedUp));
  return (
    <div className="mt-4" data-testid="sales-funnel">
      <h5>Customer funnel</h5>
      <Row className="g-3">
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title as="h6">From sign-up to paying</Card.Title>
              <Stage label="Signed up" value={f.signedUp} of={f.signedUp} />
              <Stage label="Signed in at least once" value={f.signedIn} of={f.signedUp} />
              <Stage label={`Used in the last ${f.activeDays} days`} value={f.active30} of={f.signedUp} />
              <Stage label="Has ever paid" value={f.everPaid} of={f.signedUp} />
              <Stage label="Paying now" value={f.paying} of={f.signedUp} />
              <div className="small text-muted mt-2">
                {f.trialsRunning} on a running trial, {f.trialsLapsed} trials ended without paying, {f.stopped} stopped paying
                ({f.stoppedPercent}% of those who ever paid).
              </div>
              {Object.keys(f.payingByPlan).length > 0 && (
                <div className="mt-2">
                  {Object.entries(f.payingByPlan).map(([plan, n]) => (
                    <Badge bg="light" text="dark" className="me-2 border" key={plan}>{plan}: {n}</Badge>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title as="h6">The road to {f.target.toLocaleString('en-IN')} paying customers</Card.Title>
              <div className="display-6" data-testid="funnel-months-to-target">
                {f.monthsToTarget === 0 ? 'Reached'
                  : f.monthsToTarget == null ? 'No pace yet'
                  : f.monthsToTarget > 120 ? 'Over 10 years'
                  : f.monthsToTarget >= 24 ? `${(f.monthsToTarget / 12).toFixed(1)} years`
                  : `${f.monthsToTarget} months`}
              </div>
              <div className="text-muted small mb-2">
                {f.monthsToTarget == null
                  ? 'The last three whole months added no more paying customers than they lost, so there is nothing to project from.'
                  : `At the last three whole months' pace of ${f.netPayingPerMonth} more paying customers a month (new paying less stopped).`}
              </div>
              <div>{f.paying} paying now, {f.signUpToPaidPercent}% of everyone who signed up has paid.</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Body>
          <Card.Title as="h6">By month</Card.Title>
          <div className="table-responsive">
            <Table size="sm" className="mb-0">
              <thead>
                <tr><th>Month</th><th className="text-end">Signed up</th><th className="text-end">Of them, have paid</th><th className="text-end">First paid</th><th className="text-end">Stopped</th><th /></tr>
              </thead>
              <tbody>
                {[...f.months].reverse().map((m) => (
                  <tr key={m.month}>
                    <td>{monthLabel(m.month)}</td>
                    <td className="text-end">{m.signedUp}</td>
                    <td className="text-end">{m.paid}</td>
                    <td className="text-end">{m.newPaying}</td>
                    <td className="text-end">{m.stopped}</td>
                    <td style={{ width: '30%' }}><ProgressBar now={(m.signedUp * 100) / maxSignups} style={{ height: '0.4rem' }} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="small text-muted mt-2">Recent sign-up months are still maturing: trials last a while, so their paid count will rise.</div>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Body>
          <Card.Title as="h6">Who brought them</Card.Title>
          <div className="table-responsive">
            <Table size="sm" className="mb-0">
              <thead>
                <tr><th>Partner</th><th>Kind</th><th className="text-end">Signed up</th><th className="text-end">On trial</th><th className="text-end">Paying</th><th className="text-end">Stopped</th></tr>
              </thead>
              <tbody>
                {f.partners.map((p) => (
                  <tr key={p.name}>
                    <td>{p.name}</td><td>{p.kind}</td>
                    <td className="text-end">{p.signedUp}</td><td className="text-end">{p.trial}</td>
                    <td className="text-end">{p.paying}</td><td className="text-end">{p.stopped}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
